import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Grid,
  Typography,
  Box,
} from "@mui/material";

const DEFAULT_FORM_STATE = {
  name: "",
  configuration: {},
  services: {},
};

const COURIER_SERVICES_TEMPLATES = {
  FEDEX: {
    FEDEX_PRIORITY: { min_weight: "0", max_weight: "" },
    FEDEX_PRIORITY_FREIGHT: { min_weight: "", max_weight: "" },
  },
};

/**
 * @typedef {object} AdminCourierData
 * @property {string} name - the provider name (ej: "FEDEX")
 * @property {object} configuration - json object with the credentials
 * @property {object} services - json object with the services configurated
 */

/**
 * A modal for creating a new Admin Setting Courier
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {function(AdminCourierData): Promise<void>} props.onConfirm - Async function to call with form data when saving
 * @param {boolean} props.loading - Whether the form is currently submitting
 * @param {object} props.templates - Object containing the required fields per courier
 */
const CreateSettingCourier = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  templates = {},
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  const isFormValid =
    formData.name.trim() !== "" &&
    Object.keys(templates[formData.name] || {}).every(
      (key) =>
        formData.configuration[key] &&
        formData.configuration[key].trim() !== "",
    ) &&
    (formData.name !== "FEDEX" ||
      Object.keys(formData.services).every(
        (serviceKey) =>
          formData.services[serviceKey].min_weight !== "" &&
          formData.services[serviceKey].max_weight !== "",
      ));

  useEffect(() => {
    if (open) {
      setFormData(DEFAULT_FORM_STATE);
      setFormErrors({});
    }
  }, [open]);

  const handleCourierChange = (event) => {
    const courierName = event.target.value;
    const initialConfig = templates[courierName]
      ? { ...templates[courierName] }
      : {};

    const initialServices = COURIER_SERVICES_TEMPLATES[courierName]
      ? JSON.parse(JSON.stringify(COURIER_SERVICES_TEMPLATES[courierName]))
      : {};

    setFormData({
      name: courierName,
      configuration: initialConfig,
      services: initialServices,
    });

    if (formErrors.name) {
      setFormErrors((previous) => ({ ...previous, name: null }));
    }
  };

  const handleConfigChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      configuration: {
        ...previous.configuration,
        [name]: value,
      },
    }));

    if (formErrors[name]) {
      setFormErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handleServiceWeightChange = (serviceKey, field, value) => {
    const sanitizedValue = value.replace(/-/g, "");
    setFormData((previous) => ({
      ...previous,
      services: {
        ...previous.services,
        [serviceKey]: {
          ...previous.services[serviceKey],
          [field]: sanitizedValue,
        },
      },
    }));

    if (formErrors[`${serviceKey}_${field}`]) {
      setFormErrors((previous) => ({
        ...previous,
        [`${serviceKey}_${field}`]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) {
      errors.name = "El proveedor es requerido";
      setFormErrors(errors);
      return false;
    }

    const requiredFields = Object.keys(templates[formData.name] || {});
    requiredFields.forEach((field) => {
      if (
        !formData.configuration[field] ||
        formData.configuration[field].trim() === ""
      ) {
        errors[field] = "Este campo es requerido";
      }
    });

    if (formData.name === "FEDEX") {
      const priorityMin = parseFloat(
        formData.services.FEDEX_PRIORITY?.min_weight,
      );
      const priorityMax = parseFloat(
        formData.services.FEDEX_PRIORITY?.max_weight,
      );
      const freightMin = parseFloat(
        formData.services.FEDEX_PRIORITY_FREIGHT?.min_weight,
      );
      const freightMax = parseFloat(
        formData.services.FEDEX_PRIORITY_FREIGHT?.max_weight,
      );

      if (isNaN(priorityMin) || priorityMin < 0)
        errors.FEDEX_PRIORITY_min_weight = "Mínimo inválido";
      if (isNaN(priorityMax) || priorityMax <= priorityMin)
        errors.FEDEX_PRIORITY_max_weight = "Debe ser mayor al mínimo";
      if (isNaN(freightMin) || freightMin < 0)
        errors.FEDEX_FREIGHT_min_weight = "Mínimo inválido";
      if (isNaN(freightMax) || freightMax <= freightMin)
        errors.FEDEX_FREIGHT_max_weight = "Debe ser mayor al mínimo";

      if (
        !errors.FEDEX_PRIORITY_max_weight &&
        !errors.FEDEX_FREIGHT_min_weight
      ) {
        if (
          (priorityMin <= freightMin && priorityMax >= freightMin) ||
          (freightMin <= priorityMin && freightMax >= priorityMin)
        ) {
          errors.submit =
            "Restricción de Arquitectura: Los rangos de peso no pueden solaparse ni tener valores intermedios iguales.";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    onConfirm(formData);
  };

  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isSecretField = (key) => {
    const lowerKey = key.toLowerCase();
    return (
      lowerKey.includes("secret") ||
      lowerKey.includes("password") ||
      lowerKey.includes("token") ||
      lowerKey.includes("clave")
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Agregar Proveedor Externo</DialogTitle>

      <DialogContent>
        <FormControl
          sx={{ minWidth: "550px" }}
          fullWidth
          margin="dense"
          error={!!formErrors.name}
          required
        >
          <InputLabel id="courier-select-label">Proveedor</InputLabel>
          <Select
            labelId="courier-select-label"
            id="name"
            name="name"
            value={formData.name}
            label="Proveedor"
            onChange={handleCourierChange}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {Object.keys(templates).map((courierKey) => (
              <MenuItem key={courierKey} value={courierKey}>
                {courierKey}
              </MenuItem>
            ))}
          </Select>
          {formErrors.name && (
            <FormHelperText>{formErrors.name}</FormHelperText>
          )}
        </FormControl>

        {formData.name && templates[formData.name] && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Configuración de Credenciales
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(templates[formData.name]).map((fieldKey) => (
                <Grid item xs={12} sm={12} key={fieldKey}>
                  <TextField
                    margin="dense"
                    id={fieldKey}
                    name={fieldKey}
                    label={formatLabel(fieldKey)}
                    type={isSecretField(fieldKey) ? "password" : "text"}
                    fullWidth
                    variant="outlined"
                    value={formData.configuration[fieldKey] || ""}
                    onChange={handleConfigChange}
                    error={!!formErrors[fieldKey]}
                    helperText={formErrors[fieldKey]}
                    required
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {formData.name === "FEDEX" && formData.services && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px dashed #ccc",
              borderRadius: 2,
              bgcolor: "#fafafa",
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ mb: 2, fontWeight: "bold" }}
            >
              Enrutamiento Automático por Peso
            </Typography>

            {Object.keys(formData.services).map((serviceKey) => (
              <Box key={serviceKey} sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#333" }}
                >
                  {serviceKey.replaceAll("_", " ")}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      size="small"
                      label="Peso Mínimo (kg)"
                      min="0"
                      value={formData.services[serviceKey].min_weight}
                      onChange={(e) =>
                        handleServiceWeightChange(
                          serviceKey,
                          "min_weight",
                          e.target.value,
                        )
                      }
                      error={!!formErrors[`${serviceKey}_min_weight`]}
                      helperText={formErrors[`${serviceKey}_min_weight`]}
                      inputProps={{ step: "0.1", min: "0" }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      size="small"
                      label="Peso Máximo (kg)"
                      value={formData.services[serviceKey].max_weight}
                      onChange={(e) =>
                        handleServiceWeightChange(
                          serviceKey,
                          "max_weight",
                          e.target.value,
                        )
                      }
                      error={!!formErrors[`${serviceKey}_max_weight`]}
                      helperText={formErrors[`${serviceKey}_max_weight`]}
                      inputProps={{ step: "0.1", min: "0" }}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSettingCourier;
